import { useNavigate } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import { useConfiguracionTiendaPublic } from "../hooks/useConfiguracionTiendaPublic";
import { fileURL } from "../../../utils/fileURL";
import "./Cart.css";
import { useEffect, useRef, useState, useCallback } from "react";
import { validateCart } from "../../../services/public/cartValidation";
import CheckoutAuthModal from "../../store/page/CheckoutAuthModal";
import { IconCartEmpty } from "../../../icons/Icons";

function Cart() {
  const navigate = useNavigate();
  const { formatMoney } = useConfiguracionTiendaPublic();
  const {
    cart,
    replaceCart,
    updateQty,
    remove,
    subtotal,
    coreChargeTotal,
    grandTotal,
  } = useCart();

  const [notice, setNotice] = useState(null);
  const [validating, setValidating] = useState(false);
  const isLoading = validating;
  const [showCheckoutAuth, setShowCheckoutAuth] = useState(false);
  const lastValidatedKeyRef = useRef("");

  const cartKey = useCallback((items) => {
    return (items || [])
      .map((i) => `${i.producto_id}:${i.cantidad}:${Number(i.stock ?? 0)}`)
      .join("|");
  }, []);

  const runValidation = useCallback(async () => {
    try {
      setValidating(true);

      const result = await validateCart(cart);

      let adjusted = false;

      const nextCart = cart.map((item) => {
        const validated = result.items.find(
          (i) => i.producto_id === item.producto_id
        );

        if (!validated) return item;

        if (validated.allowed !== item.cantidad) adjusted = true;

        return {
          ...item,
          nombre: item.nombre,
          cantidad: validated.allowed,
          stock: validated.available,
        };
      });

      if (adjusted) {
        replaceCart(nextCart);
        setNotice("Algunos artículos se ajustaron debido a cambios de stock.");
      } else {
        setNotice(null);
      }
    } catch (err) {
      console.error("Falló la validación del carrito", err);
      setNotice("No pudimos verificar el stock en este momento.");
    } finally {
      setValidating(false);
    }
  }, [cart, replaceCart]);

  useEffect(() => {
    if (!cart || cart.length === 0) {
      lastValidatedKeyRef.current = "";
      return;
    }

    const key = cartKey(cart);
    if (lastValidatedKeyRef.current === key) return;
    lastValidatedKeyRef.current = key;
    runValidation();
  }, [cart, cartKey, runValidation]);

  const hasOutOfStock = cart.some((item) => Number(item.stock ?? 0) === 0);
  const isEmpty = !cart || cart.length === 0;

  useEffect(() => {
    document.body.classList.toggle("cyravi-cart-empty-mode", isEmpty);
    return () => document.body.classList.remove("cyravi-cart-empty-mode");
  }, [isEmpty]);

  return (
    <div className="cyravi-cart-scope">
      {isEmpty ? (
        <div className="cyravi-cart-empty">
          <div className="cyravi-cart-empty__card">
            <div className="cyravi-cart-empty__icon">{IconCartEmpty}</div>

            <span className="cyravi-cart-empty__badge">Carrito</span>

            <h2>Tu carrito está vacío</h2>
            <p>
              Parece que aún no has agregado productos. Explora la tienda y
              encuentra lo que necesitas.
            </p>

            <button
              className="cyravi-cart-back cyravi-cart-empty__btn"
              onClick={() => navigate("/store/inicio")}
            >
              Seguir comprando
            </button>
          </div>
        </div>
      ) : (
        <div className="cyravi-cart-page">
          <div className="cyravi-cart-bg-glow cyravi-cart-bg-glow-top" />
          <div className="cyravi-cart-bg-glow cyravi-cart-bg-glow-left" />
          <div className="cyravi-cart-bg-glow cyravi-cart-bg-glow-right" />

          {isLoading && (
            <div className="cyravi-cart-overlay">
              <div className="cyravi-cart-spinner"></div>
              <p>Verificando disponibilidad de stock…</p>
            </div>
          )}

          <div className="cyravi-cart-container">
            <button
              className="cyravi-cart-back"
              onClick={() => navigate("/store/inicio")}
            >
              ← Regresar a productos
            </button>

            <div className="cyravi-cart-header">
              <span className="cyravi-cart-badge">Mi compra</span>
              <h1>Carrito de compras</h1>
              <p>
                Revisa tus productos antes de continuar con el checkout.
              </p>
            </div>

            {notice && <div className="cyravi-cart-notice">{notice}</div>}

            {validating && (
              <div className="cyravi-cart-notice cyravi-cart-notice-soft">
                Validando disponibilidad de stock…
              </div>
            )}

            <div className="cyravi-cart-content">
              <div className="cyravi-cart-list">
                {cart.map((item) => {
                  const price = Number(item.precio_unitario);
                  const core = Number(item.core_charge || 0);
                  const stock = Number(item.stock ?? 0);
                  const totalItem = price * item.cantidad;
                  const isMax = item.cantidad >= stock;
                  const outOfStock = stock === 0;

                  return (
                    <div key={item.producto_id} className="cyravi-cart-item">
                      <img
                        src={fileURL(item.imagen)}
                        alt={item.nombre}
                        className="cyravi-cart-img"
                        onClick={() =>
                          navigate(`/store/product/${item.producto_id}`)
                        }
                      />

                      <div className="cyravi-cart-info">
                        <h3>{item.nombre}</h3>

                        {item.en_oferta ? (
                          <div className="cyravi-cart-price-offer">
                            <span className="cyravi-cart-offer-badge">
                              −{item.descuento_porcentaje}%
                            </span>
                            <span className="cyravi-cart-price-old">
                              {formatMoney(Number(item.precio_original))}
                            </span>
                            <span className="cyravi-cart-price cyravi-cart-price--offer">
                              {formatMoney(price)}
                            </span>
                          </div>
                        ) : (
                          <p className="cyravi-cart-price">{formatMoney(price)}</p>
                        )}

                        {core > 0 && (
                          <p className="cyravi-cart-core">
                            Core Charge: {formatMoney(core)}
                          </p>
                        )}

                        <span
                          className={`cyravi-cart-stock ${
                            outOfStock ? "out" : isMax ? "limit" : "in"
                          }`}
                        >
                          {outOfStock ? "Agotado" : `Disponible: ${stock}`}
                        </span>

                        <button
                          className="cyravi-cart-view"
                          onClick={() =>
                            navigate(`/store/product/${item.producto_id}`)
                          }
                        >
                          Ver producto
                        </button>
                      </div>

                      <div className="cyravi-cart-qty-control">
                        <button
                          onClick={() =>
                            updateQty(
                              item.producto_id,
                              Math.max(1, item.cantidad - 1)
                            )
                          }
                          disabled={item.cantidad <= 1 || isLoading}
                          aria-label="Disminuir cantidad"
                        >
                          -
                        </button>

                        <input
                          type="number"
                          min="1"
                          max={stock}
                          value={item.cantidad}
                          disabled={outOfStock || isLoading}
                          onChange={(e) => {
                            let next = Number(e.target.value);
                            if (isNaN(next) || next < 1) next = 1;
                            if (next > stock) next = stock;
                            updateQty(item.producto_id, next);
                          }}
                          aria-label="Cantidad"
                        />

                        <button
                          onClick={() =>
                            updateQty(
                              item.producto_id,
                              Math.min(stock, item.cantidad + 1)
                            )
                          }
                          disabled={isMax || outOfStock || isLoading}
                          aria-label="Aumentar cantidad"
                        >
                          +
                        </button>
                      </div>

                      <div className="cyravi-cart-total-wrap">
                        <span>Total</span>
                        <p className="cyravi-cart-total">
                          {formatMoney(totalItem)}
                        </p>
                      </div>

                      <button
                        className="cyravi-cart-remove"
                        onClick={() => remove(item.producto_id)}
                        aria-label="Quitar del carrito"
                        title="Quitar del carrito"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>

              <aside className="cyravi-cart-summary">
                <div className="cyravi-cart-summary-top">
                  <span className="cyravi-cart-summary-badge">Resumen</span>
                  <h2>Detalle de compra</h2>
                </div>

                <div className="cyravi-cart-subtotal">
                  <span>Subtotal</span>
                  <strong>{formatMoney(subtotal)}</strong>
                </div>

                {coreChargeTotal > 0 && (
                  <div className="cyravi-cart-subtotal">
                    <span>Total de Core Charge</span>
                    <strong>{formatMoney(coreChargeTotal)}</strong>
                  </div>
                )}

                <div className="cyravi-cart-subtotal cyravi-cart-subtotal-final">
                  <span>Total</span>
                  <strong>
                    {formatMoney(coreChargeTotal > 0 ? grandTotal : subtotal)}
                  </strong>
                </div>

                <button
                  className="cyravi-cart-next-btn"
                  disabled={hasOutOfStock || validating}
                  onClick={() => setShowCheckoutAuth(true)}
                  title={
                    hasOutOfStock
                      ? "Hay artículos agotados en tu carrito"
                      : "Continuar al checkout"
                  }
                >
                  Proceder al checkout
                </button>

                <p className="cyravi-cart-summary-note">
                  El stock se validará nuevamente antes de continuar.
                </p>

                <CheckoutAuthModal
                  open={showCheckoutAuth}
                  onClose={() => setShowCheckoutAuth(false)}
                />
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;